<template lang="html">
  <v-app class="large-screen__body white">
    <div v-if="loading" class="loader-container">
      <loader></loader>
    </div>

    <div v-else>
      <v-toolbar dark flat>
        <div class="back-button-toolbar">
          <v-btn icon class="toolbar-button" @click="$router.go(-1)">
            <img src="~icons/backIcon/backIcon.png" alt="backIcon">
          </v-btn>
        </div>
        <v-spacer></v-spacer>
        <v-toolbar-title class="white--text center">
          Personal Info
        </v-toolbar-title>
        <v-spacer></v-spacer>
      </v-toolbar>

      <v-container>
        <div class="user-avatar__section">

          <v-avatar v-if="avatar" size="66" class="user__avatar">
            <img :src="avatar" :alt="username">
          </v-avatar>
          <v-avatar v-else size="66" class="primary-background">
            <span class="headline white--text">{{ userInitials }}</span>
          </v-avatar>

          <input
            @change="changeAvatar"
            type="file"
            accept="*/*"
            style="display: none;"
            ref="upload"
          >
          <v-btn class="avatar__change-button text--white" @click="launchUploader">Change Image</v-btn>
          <v-btn link flat class="avatar__remove-button" @click="avatar=null">Remove</v-btn>
        </div>

        <div class="user-edit__section">
          <v-layout row wrap class="input-container">
            <v-flex xs12 sm2>
              <label for="Title">Username</label>
            </v-flex>
            <v-flex xs12 sm10>
              <input
                type="text"
                size="45"
                placeholder="Username"
                v-model="username"
              />
            </v-flex>
          </v-layout>

          <v-layout row wrap class="input-container">
            <v-flex xs12 sm2>
              <label for="Title">Email</label>
            </v-flex>
            <v-flex xs12 sm10>
              <input
                type="email"
                size="45"
                placeholder="user@organizedeffort.com"
                v-model="email"
              />
            </v-flex>
          </v-layout>

          <v-layout row wrap class="input-container">
            <v-flex xs12 sm2>
              <label for="Title">Phone Number</label>
            </v-flex>
            <v-flex xs12 sm10>
              <input
                type="phone"
                size="45"
                placeholder="+14324255222"
                v-model="phone"
              />
            </v-flex>
          </v-layout>

          <v-layout row wrap class="input-container">
            <v-flex xs12 sm2>
              <label for="Title">Time Zone</label>
            </v-flex>
            <v-flex xs12 sm10>
              <v-select
                autocomplete
                solo dense
                placeholder="Type to search for your timezone"
                :items="timezones"
                v-model="timezone"
                >
              </v-select>
            </v-flex>
          </v-layout>

          <v-layout row wrap class="input-container">
            <v-flex xs12 sm2>
              <label for="Title">Skills</label>
            </v-flex>
            <v-flex xs12 sm10>
              <v-select
                solo
                cache-items
                autocomplete
                return-object
                multiple
                :search-input.sync="skillInput"
                class="select"
                placeholder="Type to search for skills"
                :items="skills"
                item-text="skill_name"
                item-value="skill_id"
                v-model="userSkills"
              >
              </v-select>
            </v-flex>
          </v-layout>
        </div>
      </v-container>
    </div>
  </v-app>
</template>

<script>
import moment from 'moment-timezone';
import get from 'lodash/get';
import set from 'lodash/set';
import throttle from 'lodash/throttle';
import { mapActions } from 'vuex';

import uploader from '@/mixins/uploader';
import Loader from '@/components/Loader';
import { fetchUserProfile, editUser } from '@/api';

export default {
  mixins: [uploader],
  components: {
    Loader,
  },
  data() {
    return {
      loadingProfile: false,
      profile: {},
      timezones: moment.tz.names(),
      skillInput: null,
    };
  },
  computed: {
    loading() {
      return this.loadingProfile;
    },
    id() {
      return get(this.profile, 'user_data.user_id');
    },
    avatar: {
      get() { return get(this.profile, 'user_data.avatar'); },
      set(value) {
        set(this.profile, 'user_data.avatar', value);
        this.editUserProfile();
      },
    },
    username: {
      get() { return get(this.profile, 'user_data.username'); },
      set(value) {
        set(this.profile, 'user_data.username', value);
        this.editUserProfile();
      },
    },
    email: {
      get() { return get(this.profile, 'users_profile.email'); },
      set(value) {
        set(this.profile, 'users_profile.email', value);
        this.profile = { ...this.profile };
        this.editUserProfile();
      },
    },
    phone: {
      get() { return get(this.profile, 'users_profile.phone'); },
      set(value) {
        set(this.profile, 'users_profile.phone', value);
        this.profile = { ...this.profile };
        this.editUserProfile();
      },
    },
    timezone: {
      get() { return get(this.profile, 'users_profile.timezone'); },
      set(value) {
        set(this.profile, 'users_profile.timezone', value);
        this.profile = { ...this.profile };
        this.editUserProfile();
      },
    },
    userInitials() {
      return this.username && this.username.charAt(0);
    },
    profilePayload() {
      return {
        avatar: this.avatar,
        username: this.username,
        users_profile: {
          timezone: this.timezone,
          email: this.email,
          phone: this.phone,
        },
        /* eslint arrow-body-style: ["error", "always"] */
        users_skills: this.userSkills.map((skill, i) => {
          return { ...skill, user_skills_rank: i + 1 };
        }),
      };
    },
    userSkills: {
      get() { return get(this.profile, 'users_skills', []); },
      set(value) {
        set(this.profile, 'users_skills', value);
        this.profile = { ...this.profile };
        this.editUserProfile();
      },
    },
    skills() {
      return [
        ...get(this.profile, 'users_skills', []),
        ...this.$store.getters['skills/list'],
      ];
    },
  },
  methods: {
    ...mapActions({
      searchSkills: 'skills/searchSkills',
    }),
    launchUploader() {
      this.$refs.upload.click();
    },
    changeAvatar: async function _changeAvatar(event) {
      const files = event.target.files;
      const file = files[0];
      // TODO: what to do while the image is loading
      // upload function is present in the upload mixin
      const mediaObject = await this.uploadMedia(file, { user_profile: 1 });
      this.avatar = mediaObject.data_url;
    },
    throttleEditUser: throttle(editUser, 1000),
    editUserProfile: async function _editUserProfile() {
      const token = this.$store.getters['auth/userToken'];

      try {
        const { data } = await this.throttleEditUser(token, this.profilePayload);
        const status = get(data, '[1].status');
        if (status === 'SUCCESS') {
          this.$store.dispatch('auth/setFlashMessage', {
            message: 'Profile updated.',
            type: 'success',
          });
        } else {
          this.$store.dispatch('auth/setFlashMessage', {
            message: 'Failed to update the profile.',
            type: 'error',
          });
        }
      } catch (e) {
        this.$store.dispatch('auth/setFlashMessage', {
          message: 'Failed to update the profile.',
          type: 'error',
        });
      }
    },
    fetchUserProfile: async function _fetchUserProfile() {
      this.loadingProfile = true;
      await this.$store.dispatch('auth/getUserId');
      const token = this.$store.getters['auth/userToken'];
      const userId = this.$store.getters['auth/userId'];

      try {
        const { data } = await fetchUserProfile(token, userId);
        this.loadingProfile = false;
        this.profile = get(data, '[1].data.details', {});
        if (!this.timezone) {
          this.timezone = moment.tz.guess();
        }
      } catch (e) {
        this.loadingProfile = false;
        this.$store.dispatch('auth/setFlashMessage', {
          message: 'Failed to fetch profile information.',
          type: 'error',
        });
      }
    },
  },
  watch: {
    skillInput(val) {
      if (val && val.length >= 3) {
        this.searchSkills(val);
      }
    },
  },
  mounted() {
    this.fetchUserProfile();
  },
};
</script>

<style lang="scss" scoped>
@import "~styles/shared.scss";

.loader-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-top: 50px;
}

.primary-background {
  background: linear-gradient(-90deg, $primary-red, $primary-yello);
}

.user-avatar__section {
  margin-top: 15px;
}

.user__avatar {
  margin-left: 18px;
}

.avatar__change-button {
  background: $primary;
  color: white !important;
  margin-left: 15px;
  border-radius: 24px;
}

.avatar__remove-button {
  color: $blue;
}

.user-edit__section {
  margin-top: 15px;
}

.input-container {
  margin-bottom: 15px;
  display: flex;
  align-items: center;
}

.input-container label {
  color: $blue;
}

.menu__content.menu__content--select.menu__content--autocomplete.menuable__content__active {
  top: 450px !important;
}
</style>



// WEBPACK FOOTER //
// src/components/UserProfileEdit.vue